// Шеринг PNG-картинки графика. Платформенное ветвление по образцу lib/platform.ts.
// Source of truth: docs/specs/sprint-6-sharing/stage-1-contracts.md §3 (D5–D10).
//
//   native (iOS/Android) → Filesystem.writeFile (Cache) → Share.share({ files: [uri] })
//   web с Web Share L2    → navigator.share({ files })   (AbortError = cancelled)
//   web без Web Share     → <a download> fallback

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Directory, Filesystem } from '@capacitor/filesystem';
import type { TimelinePeriod } from '@/api/types';

export type ShareResult = 'shared' | 'downloaded' | 'cancelled' | 'unsupported';

/** Детерминированное имя файла: `healthlab-<code>-<period>.png`. */
export function shareFilename(code: string, period: TimelinePeriod): string {
  const safe = code
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return `healthlab-${safe}-${period}.png`;
}

export async function sharePng(
  blob: Blob,
  filename: string,
  title: string,
): Promise<ShareResult> {
  if (Capacitor.isNativePlatform()) {
    return await shareNative(blob, filename, title);
  }
  return await shareWeb(blob, filename, title);
}

// ─── native ────────────────────────────────────────────────────────────────

async function shareNative(blob: Blob, filename: string, title: string): Promise<ShareResult> {
  const base64 = await blobToBase64(blob);
  // Cache dir — система чистит сама, файл живёт ровно для передачи в share-sheet.
  const written = await Filesystem.writeFile({
    path: filename,
    data: base64,
    directory: Directory.Cache,
  });
  try {
    // Только files: на iOS `url` + `files` дают ДВА activity item'а (дубль PNG),
    // на Android тот же файл проходит через shareFiles дважды. PNG = файл, не ссылка.
    await Share.share({ title, files: [written.uri] });
    return 'shared';
  } catch (err) {
    // Юзер закрыл share-sheet — это не ошибка (D10).
    if (isShareCancellation(err)) return 'cancelled';
    throw err;
  }
}

// ─── web ─────────────────────────────────────────────────────────────────────

async function shareWeb(blob: Blob, filename: string, title: string): Promise<ShareResult> {
  const file = new File([blob], filename, { type: 'image/png' });
  const nav = navigator as Navigator & {
    canShare?: (data?: ShareData) => boolean;
    share?: (data?: ShareData) => Promise<void>;
  };

  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ title, files: [file] });
      return 'shared';
    } catch (err) {
      if (isShareCancellation(err)) return 'cancelled';
      throw err;
    }
  }

  // Fallback: скачивание (десктоп без Web Share files).
  return downloadBlob(blob, filename);
}

function downloadBlob(blob: Blob, filename: string): ShareResult {
  if (typeof document === 'undefined') return 'unsupported';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return 'downloaded';
}

// ─── helpers ───────────────────────────────────────────────────────────────

/** Web Share / native плагины кидают AbortError при отмене юзером. */
function isShareCancellation(err: unknown): boolean {
  if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'AbortError') {
    return true;
  }
  const msg = err instanceof Error ? err.message : String(err);
  return /abort|cancel|canceled|cancelled|dismiss/i.test(msg);
}

/** Blob → чистый base64 (без `data:...;base64,` префикса) для Filesystem.writeFile. */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}